import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../utils/api';
import { 
  DollarSign, CheckCircle, XCircle, Clock, Filter,
  ChevronDown, Calendar, Users, TrendingUp, Download,
  Check, X, AlertCircle, Wallet, BarChart3
} from 'lucide-react';

const API_URL = getApiUrl();

const CommissionsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('summary');
  const [pendingCommissions, setPendingCommissions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedIds, setSelectedIds] = useState([]);
  const [processing, setProcessing] = useState(false);

  const [recalculating, setRecalculating] = useState(false);

  // Funnel state
  const [funnelData, setFunnelData] = useState(null);
  const [funnelDoctor, setFunnelDoctor] = useState('');
  const [funnelStartDate, setFunnelStartDate] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [funnelEndDate, setFunnelEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [loadingFunnel, setLoadingFunnel] = useState(false);

  useEffect(() => {
    if (activeTab === 'summary') {
      fetchFunnel();
      fetchData();
    } else {
      fetchData();
    }
  }, [activeTab, selectedMonth, selectedYear]);

  const handleRecalculateGST = async () => {
    if (!window.confirm('This will recalculate ALL commissions using base amount (total ÷ 1.18). This can only run once. Continue?')) return;
    setRecalculating(true);
    try {
      const res = await axios.post(`${API_URL}/api/doctors/commissions/recalculate-gst`, {}, { withCredentials: true });
      toast.success(`Recalculated ${res.data.details?.length || 0} commissions`);
      fetchData();
      fetchFunnel();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Recalculation failed');
    } finally {
      setRecalculating(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, summaryRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/doctors/commissions/pending`, { withCredentials: true }),
        axios.get(`${API_URL}/api/doctors/commissions/summary?month=${selectedMonth}&year=${selectedYear}`, { withCredentials: true })
      ]);
      if (pendingRes.status === 'fulfilled') setPendingCommissions(pendingRes.value.data);
      if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value.data);
    } catch (error) {
      toast.error('Failed to fetch commission data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFunnel = async () => {
    setLoadingFunnel(true);
    try {
      const params = new URLSearchParams();
      if (funnelDoctor) params.append('doctor_id', funnelDoctor);
      if (funnelStartDate) params.append('start_date', funnelStartDate);
      if (funnelEndDate) params.append('end_date', funnelEndDate);
      const r = await axios.get(`${API_URL}/api/doctors/funnel?${params}`, { withCredentials: true });
      setFunnelData(r.data);
    } catch (error) {
      toast.error('Failed to load funnel data');
    } finally {
      setLoadingFunnel(false);
    }
  };

  const handleApprove = async (referralId) => {
    try {
      await axios.post(`${API_URL}/api/doctors/commissions/${referralId}/approve`, {}, { withCredentials: true });
      toast.success('Commission approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve commission');
    }
  };

  const handleReject = async (referralId) => {
    try {
      await axios.post(`${API_URL}/api/doctors/commissions/${referralId}/reject`, {}, { withCredentials: true });
      toast.success('Commission rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject commission');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) { toast.error('Select at least one commission'); return; }
    setProcessing(true);
    try {
      await axios.post(`${API_URL}/api/doctors/commissions/bulk-approve`, selectedIds, { withCredentials: true });
      toast.success(`Approved ${selectedIds.length} commissions`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to bulk approve');
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkPaid = async () => {
    if (selectedIds.length === 0) { toast.error('Select at least one commission'); return; }
    setProcessing(true);
    try {
      await axios.post(`${API_URL}/api/doctors/commissions/mark-paid`, selectedIds, { withCredentials: true });
      toast.success(`Marked ${selectedIds.length} commissions as paid`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to mark as paid');
    } finally {
      setProcessing(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    setSelectedIds(prev => prev.length === pendingCommissions.length ? [] : pendingCommissions.map(c => c.referral_id));
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div data-testid="commissions-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Commission Management</h1>
          <p className="text-slate-500">Doctor referral funnel, commissions & payouts</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-6">
          {['summary', 'pending'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab === 'pending' ? `Pending Approval (${pendingCommissions.length})` : 'Doctor Summary'}
            </button>
          ))}
        </nav>
      </div>

      {/* ==================== DOCTOR SUMMARY TAB (Funnel + Commissions) ==================== */}
      {activeTab === 'summary' && (
        <div className="space-y-6">
          {/* Funnel Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" /> Doctor Referral Funnel
              </h3>
            </div>

            {/* Funnel Filters */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[180px]">
                <label className="block text-xs font-medium text-slate-500 mb-1">Doctor</label>
                <select value={funnelDoctor} onChange={(e) => setFunnelDoctor(e.target.value)} className="input-base" data-testid="funnel-doctor-filter">
                  <option value="">All Doctors</option>
                  {funnelData?.all_doctors?.map(d => (
                    <option key={d.doctor_id} value={d.doctor_id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
                <input type="date" value={funnelStartDate} onChange={(e) => setFunnelStartDate(e.target.value)} className="input-base" data-testid="funnel-start-date" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
                <input type="date" value={funnelEndDate} onChange={(e) => setFunnelEndDate(e.target.value)} className="input-base" data-testid="funnel-end-date" />
              </div>
              <button onClick={fetchFunnel} className="btn-primary text-sm h-10" data-testid="funnel-apply-btn">Apply</button>
            </div>

            {/* Funnel KPIs */}
            {funnelData?.totals && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  { label: 'Leads Referred', value: funnelData.totals.total_leads, icon: Users, color: 'indigo' },
                  { label: 'Trial Completed', value: funnelData.totals.trial_completed, icon: CheckCircle, color: 'blue' },
                  { label: 'Consultation Done', value: funnelData.totals.consultation_completed, icon: Calendar, color: 'purple' },
                  { label: 'Enrolled', value: funnelData.totals.enrolled, icon: TrendingUp, color: 'emerald' },
                  { label: 'Revenue', value: `₹${(funnelData.totals.total_revenue || 0).toLocaleString('en-IN')}`, icon: DollarSign, color: 'green' },
                ].map((kpi) => (
                  <div key={kpi.label} className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 bg-${kpi.color}-100 rounded-lg flex items-center justify-center`}>
                        <kpi.icon className={`w-5 h-5 text-${kpi.color}-600`} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">{kpi.value}</p>
                        <p className="text-xs text-slate-500">{kpi.label}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Funnel Table */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="table-header">
                    <tr>
                      <th className="p-4 text-left">Doctor</th>
                      <th className="p-4 text-center">Leads Referred</th>
                      <th className="p-4 text-center">Trial Completed</th>
                      <th className="p-4 text-center">Consultation Done</th>
                      <th className="p-4 text-center">Enrolled</th>
                      <th className="p-4 text-right">Revenue</th>
                      <th className="p-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingFunnel ? (
                      <tr><td colSpan="7" className="p-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div></td></tr>
                    ) : !funnelData?.doctors?.length ? (
                      <tr><td colSpan="7" className="p-8 text-center text-slate-500">No doctor referral data for this period</td></tr>
                    ) : (
                      funnelData.doctors.map((d) => (
                        <tr key={d.doctor_id} className="table-row">
                          <td className="p-4 font-medium text-slate-900">{d.doctor_name}</td>
                          <td className="p-4 text-center">{d.total_leads}</td>
                          <td className="p-4 text-center">
                            <span className="inline-flex items-center">
                              {d.trial_completed}
                              {d.total_leads > 0 && <span className="text-xs text-slate-400 ml-1">({Math.round(d.trial_completed / d.total_leads * 100)}%)</span>}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <span className="inline-flex items-center">
                              {d.consultation_completed}
                              {d.total_leads > 0 && <span className="text-xs text-slate-400 ml-1">({Math.round(d.consultation_completed / d.total_leads * 100)}%)</span>}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <span className="inline-flex items-center">
                              {d.enrolled}
                              {d.total_leads > 0 && <span className="text-xs text-slate-400 ml-1">({Math.round(d.enrolled / d.total_leads * 100)}%)</span>}
                            </span>
                          </td>
                          <td className="p-4 text-right font-semibold text-green-600">₹{(d.total_revenue || 0).toLocaleString('en-IN')}</td>
                          <td className="p-4 text-center">
                            <button
                              onClick={() => navigate(`/leads?doctor_id=${d.doctor_id}`)}
                              className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                              data-testid={`view-leads-${d.doctor_id}`}
                            >
                              <Users className="w-3.5 h-3.5" />
                              View Leads
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {funnelData?.doctors?.length > 1 && (
                    <tfoot className="bg-slate-50">
                      <tr>
                        <td className="p-4 font-bold text-slate-900">Total</td>
                        <td className="p-4 text-center font-bold">{funnelData.totals.total_leads}</td>
                        <td className="p-4 text-center font-bold">{funnelData.totals.trial_completed}</td>
                        <td className="p-4 text-center font-bold">{funnelData.totals.consultation_completed}</td>
                        <td className="p-4 text-center font-bold">{funnelData.totals.enrolled}</td>
                        <td className="p-4 text-right font-bold text-green-600">₹{(funnelData.totals.total_revenue || 0).toLocaleString('en-IN')}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>

          {/* Commission Summary Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Commission Summary for {months[selectedMonth - 1]} {selectedYear}</h3>
              <div className="flex gap-3">
                <select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))} className="input-base">
                  {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
                <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} className="input-base">
                  {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>

            {summary && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  { label: 'Conversions', value: summary.totals?.total_conversions || 0, icon: Users, color: 'indigo' },
                  { label: 'Gross', value: `₹${(summary.totals?.gross_commission || 0).toLocaleString()}`, icon: DollarSign, color: 'green' },
                  { label: 'Pending', value: `₹${(summary.totals?.pending_commission || 0).toLocaleString()}`, icon: Clock, color: 'amber' },
                  { label: 'Approved', value: `₹${(summary.totals?.approved_commission || 0).toLocaleString()}`, icon: CheckCircle, color: 'blue' },
                  { label: 'Paid Out', value: `₹${(summary.totals?.paid_commission || 0).toLocaleString()}`, icon: Wallet, color: 'emerald' },
                ].map(kpi => (
                  <div key={kpi.label} className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 bg-${kpi.color}-100 rounded-lg flex items-center justify-center`}>
                        <kpi.icon className={`w-5 h-5 text-${kpi.color}-600`} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">{kpi.value}</p>
                        <p className="text-xs text-slate-500">{kpi.label}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-x-auto">
              <table className="w-full">
                <thead className="table-header">
                  <tr>
                    <th className="p-4 text-left">Doctor</th>
                    <th className="p-4 text-left">Conversions</th>
                    <th className="p-4 text-left">Gross</th>
                    <th className="p-4 text-left">Pending</th>
                    <th className="p-4 text-left">Approved</th>
                    <th className="p-4 text-left">Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {!summary?.summaries?.length ? (
                    <tr><td colSpan="6" className="p-8 text-center text-slate-500">No commission data for this period</td></tr>
                  ) : summary.summaries.map(doc => (
                    <tr key={doc.doctor_id} className="table-row">
                      <td className="table-cell"><div className="font-medium text-slate-900">{doc.doctor_name}</div><div className="text-xs text-slate-500">{doc.doctor_email}</div></td>
                      <td className="table-cell font-medium">{doc.total_conversions}</td>
                      <td className="table-cell font-semibold text-slate-900">₹{doc.gross_commission.toLocaleString()}</td>
                      <td className="table-cell text-amber-600 font-medium">₹{doc.pending_commission.toLocaleString()}</td>
                      <td className="table-cell text-blue-600 font-medium">₹{doc.approved_commission.toLocaleString()}</td>
                      <td className="table-cell text-green-600 font-medium">₹{doc.paid_commission.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                {summary?.summaries?.length > 0 && (
                  <tfoot className="bg-slate-50">
                    <tr>
                      <td className="p-4 font-bold">Total</td>
                      <td className="p-4 font-bold">{summary.totals?.total_conversions}</td>
                      <td className="p-4 font-bold">₹{summary.totals?.gross_commission.toLocaleString()}</td>
                      <td className="p-4 font-bold text-amber-600">₹{summary.totals?.pending_commission.toLocaleString()}</td>
                      <td className="p-4 font-bold text-blue-600">₹{summary.totals?.approved_commission.toLocaleString()}</td>
                      <td className="p-4 font-bold text-green-600">₹{summary.totals?.paid_commission.toLocaleString()}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      )}
      {activeTab === 'pending' && (
        <div className="space-y-4">
          <div className="flex items-center justify-end gap-3">
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))} className="input-base">
              {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} className="input-base">
              {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200">
            {pendingCommissions.length > 0 && (
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={selectedIds.length === pendingCommissions.length && pendingCommissions.length > 0} onChange={toggleSelectAll} className="w-4 h-4 rounded border-slate-300" />
                  <span className="text-sm text-slate-600">{selectedIds.length > 0 ? `${selectedIds.length} selected` : 'Select all'}</span>
                </div>
                {selectedIds.length > 0 && (
                  <div className="flex items-center gap-2">
                    <button onClick={handleBulkApprove} disabled={processing} className="btn-primary text-sm flex items-center gap-1"><Check className="w-4 h-4" /> Approve</button>
                    <button onClick={handleMarkPaid} disabled={processing} className="text-sm px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-1"><Wallet className="w-4 h-4" /> Mark Paid</button>
                  </div>
                )}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="table-header">
                  <tr>
                    <th className="p-4 text-left w-12"></th>
                    <th className="p-4 text-left">Doctor</th>
                    <th className="p-4 text-left">Lead</th>
                    <th className="p-4 text-left">Enrollment Amount</th>
                    <th className="p-4 text-left">Commission</th>
                    <th className="p-4 text-left">Date</th>
                    <th className="p-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="7" className="p-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div></td></tr>
                  ) : pendingCommissions.length === 0 ? (
                    <tr><td colSpan="7" className="p-8 text-center"><CheckCircle className="w-12 h-12 text-green-200 mx-auto mb-2" /><p className="text-slate-500">No pending commissions</p></td></tr>
                  ) : pendingCommissions.map((c) => (
                    <tr key={c.referral_id} className="table-row">
                      <td className="table-cell"><input type="checkbox" checked={selectedIds.includes(c.referral_id)} onChange={() => toggleSelect(c.referral_id)} className="w-4 h-4 rounded border-slate-300" /></td>
                      <td className="table-cell"><div className="font-medium text-slate-900">{c.doctor_name}</div><div className="text-xs text-slate-500">{c.doctor_email}</div></td>
                      <td className="table-cell"><div className="text-slate-900">{c.lead_name}</div><div className="text-xs text-slate-500">{c.lead_phone}</div></td>
                      <td className="table-cell font-medium text-slate-900">₹{(c.enrolled_amount || 0).toLocaleString()}</td>
                      <td className="table-cell"><div className="font-semibold text-green-600">₹{(c.commission_amount || 0).toLocaleString()}</div><div className="text-xs text-slate-500">{c.commission_percentage}%</div></td>
                      <td className="table-cell text-sm text-slate-600">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</td>
                      <td className="table-cell"><div className="flex items-center gap-1">
                        <button onClick={() => handleApprove(c.referral_id)} className="p-1.5 text-green-600 hover:bg-green-50 rounded-md" title="Approve"><Check className="w-4 h-4" /></button>
                        <button onClick={() => handleReject(c.referral_id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-md" title="Reject"><X className="w-4 h-4" /></button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CommissionsPage;
